
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import Libraries from 'plaid-threads/Icons/Libraries';
import External from 'plaid-threads/Icons/External';
import Actions from '../../../components/docs/Actions';
import Overview from '../../../components/docs/Overview';
import Layout from '../../../components/docs/Layout';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Retrieve up to 24 months of transaction data and stay up-to-date with webhooks'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Overview.Header product={'Transactions'}>
  {metadata.description}
    </Overview.Header>
    <Actions.SmallWrapper miniTitle='Get started with Transactions'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/transactions/' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Quickstart' link='/docs/quickstart' />
  <Actions.SmallCard icon={<External mdxType="External" />} title='Demo' link='https://plaid.coastdemo.com/share/67e1889391fb8841d04eb6ba?zoom=100' /> 
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Transactions data can be useful for many different applications, including
personal finance management, expense reporting, cash flow modeling, risk
analysis, and more. Plaid's Transactions product allows you to access a user's
transaction history for `}<inlineCode parentName="p">{`depository`}</inlineCode>{` type accounts such as checking and savings
accounts, `}<inlineCode parentName="p">{`credit`}</inlineCode>{` type accounts such as credit cards, and `}<inlineCode parentName="p">{`loan`}</inlineCode>{` type accounts
such as mortgages and student loans. For transaction history from investment accounts, use Plaid's
`}<a parentName="p" {...{
        "href": "/docs/investments"
      }}>{`Investments`}</a>{` product.`}</p>
    <YouTubeEmbed videoId='hBiKJ6vTa4g' alignment='full' title='Plaid Transactions in Three Minutes' mdxType="YouTubeEmbed" />
    <p>{`Transactions data available via `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` includes transaction date, amount, category, merchant,
location, and more. For example data, see the `}<a parentName="p" {...{
        "href": "/docs/api/products/transactions/#transactions-sync-response-transactions-update-status"
      }}>{`Transactions API reference`}</a>{`.`}</p>
    <h5 {...{
      "id": "typical-fill-rates-for-selected-fields"
    }}>{`Typical fill rates for selected fields`}</h5>
    <p>{`Below are typical fill rates for selected fields returned by Transactions. Not all fields are included in the table below. `}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Field`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Typical fill rate`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Amount`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`100%`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Date`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`100%`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Description`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`100%`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Merchant name`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`97%*`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Category (`}<inlineCode parentName="td">{`personal_finance_category`}</inlineCode>{`)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`95%`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`*Denominator excludes transactions that do not have an associated merchant, such as cash transactions, direct deposits, or bank fees.`}</p>
    <h5 {...{
      "id": "how-much-transaction-history-is-available"
    }}>{`How much transaction history is available`}</h5>
    <p>{`When Transactions is first initialized on an Item, `}<a parentName="p" {...{
        "href": "/docs/api/link/#link-token-create-request-transactions-days-requested"
      }}><inlineCode parentName="a">{`days_requested`}</inlineCode></a>{` controls how many days of history Plaid requests from the financial institution in the initial pull. The default is 90 days and the maximum is 730 days (about 24 months).`}</p>
    <p>{`Once the Item exists, Plaid retains every new transaction it sees, so the amount of available history grows for as long as the Item remains connected. `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` returns everything Plaid has stored for the Item, and the `}<inlineCode parentName="p">{`start_date`}</inlineCode>{` parameter on `}<inlineCode parentName="p">{`/transactions/get`}</inlineCode>{` has no age limit. For example, an Item created two years ago with `}<inlineCode parentName="p">{`days_requested`}</inlineCode>{` set to 730 can return roughly four years of transactions today.`}</p>
    <h4 {...{
      "id": "integration-overview"
    }}>{`Integration overview`}</h4>
    <p>{`The steps below show an overview of how to integrate Transactions. For a detailed, step-by-step view, you can also watch our full-length, comprehensive tutorial walkthrough.`}</p>
    <YouTubeEmbed videoId='Pin0-ceDKcI' alignment='full' title='Plaid Transactions full integration tutorial' mdxType="YouTubeEmbed" />
    <Callout title="Linking multiple institutions in one session" mdxType="Callout">
      <p>{`If your use case involves seeing a user's full financial picture across multiple institutions, you can use `}<a parentName="p" {...{
          "href": "/docs/link/multi-item-link"
        }}>{`Multi-Item Link`}</a>{` to let users link multiple accounts in a single Link session.`}</p>
    </Callout>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`. Along with any other parameters you specify, make sure to include the following:`}</p>
        <ul parentName="li">
          <li parentName="ul">{`The `}<inlineCode parentName="li">{`products`}</inlineCode>{` array should include `}<inlineCode parentName="li">{`transactions`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`Specify a `}<inlineCode parentName="li">{`webhook`}</inlineCode>{` receiver endpoint to receive transactions updates.`}</li>
          <li parentName="ul">{`If your use case involves a user linking Items at multiple institutions, consider using `}<a parentName="li" {...{
              "href": "/docs/link/multi-item-link"
            }}>{`Multi-Item Link`}</a>{`.`}</li>
          <li parentName="ul">{`Specify a value for `}<inlineCode parentName="li">{`transactions.days_requested`}</inlineCode>{` corresponding to the amount of transaction history your integration needs. The more transaction history is requested, the longer the historical update poll will take. The default value is 90; the maximum value is 730.`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`On the client side, create an instance of Link using the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` returned by `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`; for more details, see the `}<a parentName="p" {...{
            "href": "/docs/link"
          }}>{`Link documentation`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Once the end user has completed the Link flow, exchange the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` by calling `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Create a method to call `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{`. This method must do the following:`}</p>
        <ul parentName="li">
          <li parentName="ul">{`The first time `}<inlineCode parentName="li">{`/transactions/sync`}</inlineCode>{` is called on an Item, call `}<inlineCode parentName="li">{`/transactions/sync`}</inlineCode>{` with no cursor value specified`}</li>
          <li parentName="ul">{`Save the `}<inlineCode parentName="li">{`next_cursor`}</inlineCode>{` value returned by `}<inlineCode parentName="li">{`/transactions/sync`}</inlineCode>{` to use as an input parameter in the next call to `}<inlineCode parentName="li">{`/transactions/sync`}</inlineCode></li>
          <li parentName="ul">{`If `}<inlineCode parentName="li">{`has_more`}</inlineCode>{` in the `}<inlineCode parentName="li">{`/transactions/sync`}</inlineCode>{` response is `}<inlineCode parentName="li">{`true`}</inlineCode>{`, handle paginated results by temporarily preserving the cursor you originally passed in, then calling `}<inlineCode parentName="li">{`/transactions/sync`}</inlineCode>{` again using the cursor returned in the response. Repeat, each time using the cursor returned from the previous call, until `}<inlineCode parentName="li">{`has_more`}</inlineCode>{` is `}<inlineCode parentName="li">{`false`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`When handling paginated results, if `}<inlineCode parentName="li">{`TRANSACTIONS_SYNC_MUTATION_DURING_PAGINATION`}</inlineCode>{` error is returned during pagination, restart the process in the previous bullet with the old cursor that was temporarily preserved. Once `}<inlineCode parentName="li">{`has_more`}</inlineCode>{` is `}<inlineCode parentName="li">{`false`}</inlineCode>{`, it is safe to stop preserving the old cursor.`}</li>
        </ul>
        <p parentName="li">{`For an example, see `}<a parentName="p" {...{
            "href": "https://github.com/plaid/tutorial-resources/blob/main/transactions/finished/server/routes/transactions.js#L31"
          }}>{`fetchNewSyncData`}</a>{` and `}<a parentName="p" {...{
            "href": "https://github.com/plaid/tutorial-resources/blob/main/transactions/finished/server/routes/transactions.js#L110"
          }}>{`syncTransactions`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Call the `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` wrapper you created in the previous step, passing in the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, in order to activate the Item for the `}<a parentName="p" {...{
            "href": "/docs/api/products/transactions/#sync_updates_available"
          }}><inlineCode parentName="a">{`SYNC_UPDATES_AVAILABLE`}</inlineCode></a>{` webhook. It is common that no transactions will be returned during this first call, as it takes Plaid some time to fetch initial transactions.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`(Optional) Wait for the `}<a parentName="p" {...{
            "href": "/docs/api/products/transactions/#sync_updates_available"
          }}><inlineCode parentName="a">{`SYNC_UPDATES_AVAILABLE`}</inlineCode></a>{` webhook to fire with the `}<inlineCode parentName="p">{`initial_update_complete`}</inlineCode>{` field set to `}<inlineCode parentName="p">{`true`}</inlineCode>{` and the `}<inlineCode parentName="p">{`historical_update_complete`}</inlineCode>{` set to `}<inlineCode parentName="p">{`false`}</inlineCode>{`. When this occurs, you may optionally call your `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` wrapper to obtain the most recent 30 days of data. If your end-user is expecting to see data load into your app in real-time, this can help improve the responsiveness of your app. `}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Wait for the `}<a parentName="p" {...{
            "href": "/docs/api/products/transactions/#sync_updates_available"
          }}><inlineCode parentName="a">{`SYNC_UPDATES_AVAILABLE`}</inlineCode></a>{` webhook to fire with the `}<inlineCode parentName="p">{`historical_update_complete`}</inlineCode>{` field set to `}<inlineCode parentName="p">{`true`}</inlineCode>{`. Once it does, call your `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` wrapper to obtain all available updates. `}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`From this point forward, Plaid will periodically check for transactions data for your Item. When it detects that new transactions data is available, the `}<a parentName="p" {...{
            "href": "/docs/api/products/transactions/#sync_updates_available"
          }}><inlineCode parentName="a">{`SYNC_UPDATES_AVAILABLE`}</inlineCode></a>{` webhook will fire again. When it does, call your `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` wrapper to receive all updates since the `}<inlineCode parentName="p">{`next_cursor`}</inlineCode>{` value. In addition to added transactions, these subsequent updates may also include `}<inlineCode parentName="p">{`removed`}</inlineCode>{` or `}<inlineCode parentName="p">{`modified`}</inlineCode>{` transactions. For details, see `}<a parentName="p" {...{
            "href": "#transactions-updates"
          }}>{`Transactions updates`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`(Optional) If you would like to check for updates more frequently than Plaid's default schedule, call `}<inlineCode parentName="p">{`/transactions/refresh`}</inlineCode>{` to force an on-demand update via the optional `}<a parentName="p" {...{
            "href": "#transactions-refresh"
          }}>{`Transactions Refresh`}</a>{` add-on. If any new transaction data is available, a `}<a parentName="p" {...{
            "href": "/docs/api/products/transactions/#sync_updates_available"
          }}><inlineCode parentName="a">{`SYNC_UPDATES_AVAILABLE`}</inlineCode></a>{` webhook will fire. `}</p>
      </li>
    </ol>
    <h4 {...{
      "id": "transactions-updates"
    }}>{`Transactions updates`}</h4>
    <p>{`Transactions data is not static. As time passes, your users will make new
transactions, and transactions they made in the past will change as they are
processed by the financial institution. To learn more about how transactions are
processed and can change, see
`}<a parentName="p" {...{
        "href": "/docs/transactions/transactions-data"
      }}>{`Transaction states`}</a>{`.`}</p>
    <p>{`Plaid checks for updated transactions regularly, and uses
webhooks to notify you of any changes so you can keep your app up to date. For
more detail on how to listen and respond to transaction update webhooks, see
`}<a parentName="p" {...{
        "href": "/docs/transactions/webhooks"
      }}>{`Transaction webhooks`}</a>{`.`}</p>
    <p>{`The frequency of transactions update checks is typically between one and four times per day, depending on the institution. To
learn when an Item was last checked for updates, you can view the Item in the
`}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/activity/debugger"
      }}>{`Item Debugger`}</a>{`. If you would
like to display this information in your app's UI to help users understand the
freshness of their data, it can also be retrieved via API, using the `}<inlineCode parentName="p">{`/item/get`}</inlineCode>{`
endpoint.`}</p>
    <h5 {...{
      "id": "transactions-refresh"
    }}>{`Transactions refresh`}</h5>
    <p>{`You can also request an update on-demand via the `}<inlineCode parentName="p">{`/transactions/refresh`}</inlineCode>{`
endpoint, which is available as an add-on for Transactions customers. To request
access to this endpoint, submit a
`}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/team/products"
      }}>{`product access request`}</a>{` or contact
your Plaid account manager.`}</p>
    <h4 {...{
      "id": "recurring-transactions"
    }}>{`Recurring transactions`}</h4>
    <p>{`If your app involves personal financial management functionality, you may want
to view a summary of a user's inflows and outflows. The
`}<inlineCode parentName="p">{`/transactions/recurring/get`}</inlineCode>{` endpoint provides a summary of the recurring
outflow and inflow streams and includes insights about each recurring stream
including the category, merchant, last amount, and more.
`}<inlineCode parentName="p">{`/transactions/recurring/get`}</inlineCode>{` is available as an add-on for Transactions
customers in the US, Canada, and UK and is supported by all the same institutions in those countries that support the Transactions product. To request access to this endpoint, submit
a `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/team/products"
      }}>{`product access request`}</a>{` or contact
your Plaid account manager.`}</p>
    <h4 {...{
      "id": "sample-app-demo-and-code"
    }}>{`Sample app demo and code`}</h4>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://github.com/plaid/tutorial-resources/tree/main/transactions"
      }}>{`Transactions sample app`}</a>{` is a simple app designed to accompany the `}<a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=Pin0-ceDKcI"
      }}>{`Transactions YouTube tutorial`}</a>{`.`}</p>
    <p>{`For a more robust example of an app that incorporates transactions, along with
sample code for transactions reconciliation, see the Node-based
`}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern"
      }}>{`Plaid Pattern`}</a>{` sample app.`}</p>
    <h4 {...{
      "id": "testing-transactions-in-sandbox"
    }}>{`Testing Transactions in Sandbox`}</h4>
    <p>{`Plaid provides several special users for testing Transactions. One example is `}<inlineCode parentName="p">{`user_transactions_dynamic`}</inlineCode>{` (password: any non-blank password). You can create a public token for this user using `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{`; if using the interactive Link flow, you must use a non-OAuth test institution such as First Platypus Bank (`}<inlineCode parentName="p">{`ins_109508`}</inlineCode>{`). `}</p>
    <p>{`Unlike `}<inlineCode parentName="p">{`user_good`}</inlineCode>{` or the persona-based Transactions test accounts, `}<inlineCode parentName="p">{`user_transactions_dynamic`}</inlineCode>{` generates transaction updates on each call to `}<inlineCode parentName="p">{`/transactions/refresh`}</inlineCode>{`. This creates new pending transactions, moves existing pending transactions to posted, and triggers webhooks. For more details on how to simulate transaction activity with this user, see `}<a parentName="p" {...{
        "href": "/docs/transactions/transactions-data/#testing-pending-and-posted-transactions"
      }}>{`Testing pending and posted transactions`}</a>{`.`}</p>
    <p>{`If you are using the `}<a parentName="p" {...{
        "href": "/docs/api/products/transactions/#transactionsrecurringget"
      }}>{`Recurring Transactions add-on`}</a>{`, you can also use `}<inlineCode parentName="p">{`user_transactions_dynamic`}</inlineCode>{` to test recurring transactions in Sandbox. Its checking and credit card accounts have six months of recurring transactions. Calling `}<inlineCode parentName="p">{`/transactions/refresh`}</inlineCode>{` or `}<inlineCode parentName="p">{`/sandbox/transactions/create`}</inlineCode>{` will typically not trigger a `}<a parentName="p" {...{
        "href": "/docs/api/products/transactions/#recurring_transactions_update"
      }}><inlineCode parentName="a">{`RECURRING_TRANSACTIONS_UPDATE`}</inlineCode></a>{` webhook, since this webhook only fires when a recurring stream is detected or changes. To test your webhook handler, use `}<inlineCode parentName="p">{`/sandbox/item/fire_webhook`}</inlineCode>{` to fire `}<inlineCode parentName="p">{`RECURRING_TRANSACTIONS_UPDATE`}</inlineCode>{`.`}</p>
    <p>{`To add your own transactions to any Sandbox Item, including `}<inlineCode parentName="p">{`user_good`}</inlineCode>{`, `}<inlineCode parentName="p">{`user_transactions_dynamic`}</inlineCode>{`, and `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/"
      }}>{`custom users`}</a>{`, call `}<inlineCode parentName="p">{`/sandbox/transactions/create`}</inlineCode>{`. This simulates a `}<inlineCode parentName="p">{`/transactions/refresh`}</inlineCode>{` call in which Plaid sees those transactions as new, and all corresponding webhooks fire. Each transaction is added to the account named by its `}<inlineCode parentName="p">{`account_id`}</inlineCode>{`, or, if you omit it, to the Item's first depository account (or its first account, if it has no depository account).`}</p>
    <p>{`For persona-based testing, Plaid also provides `}<inlineCode parentName="p">{`user_ewa_user`}</inlineCode>{`, `}<inlineCode parentName="p">{`user_yuppie`}</inlineCode>{`, and `}<inlineCode parentName="p">{`user_small_business`}</inlineCode>{` users. These accounts simulate real life personas, so new transactions will appear at a more realistic rate and will not appear every time `}<inlineCode parentName="p">{`/transactions/refresh`}</inlineCode>{` is called. These users have three months of transactions, including some recurring transactions. These users also require a non-OAuth test institution.`}</p>
    <p>{`All users below should be used with any non-blank password and a non-OAuth institution, such as First Platypus Bank (`}<inlineCode parentName="p">{`ins_109508`}</inlineCode>{`).`}</p>
    <Layout.FullWidth>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`User Name`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Transaction Data`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`New txns on refresh`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Recurring`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`user_transactions_dynamic`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Dynamic data`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Triggers webhooks, allows adding custom transactions`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Always`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes (6 months)`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`user_ewa_user`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Earned-wage access persona`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`3 months of realistic data`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Sometimes`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes (some)`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`user_yuppie`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Young affluent professional`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`3 months of realistic data`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Sometimes`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes (some)`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`user_small_business`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Small business persona`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`3 months of realistic data`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Sometimes`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes (some)`}</td>
          </tr>
        </tbody>
      </table>
    </Layout.FullWidth>
    <h4 {...{
      "id": "transactions-pricing"
    }}>{`Transactions pricing`}</h4>
    <p>{`Transactions and the optional Recurring Transactions add-on are billed on a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#subscription-fee"
      }}>{`subscription model`}</a>{`. The optional Transactions Refresh add-on is billed on a `}<a parentName="p" {...{
        "href": "/docs/account/billing#per-request-flat-fee"
      }}>{`per-request model`}</a>{`. To view the exact pricing you may be eligible for, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/overview/production"
      }}>{`apply for Production access`}</a>{` or `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{`. For more details about pricing and billing models, see `}<a parentName="p" {...{
        "href": "/docs/account/billing"
      }}>{`Plaid billing`}</a>{`.`}</p>
    <h4 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h4>
    <p>{`To get started building with Transactions, see
`}<a parentName="p" {...{
        "href": "/docs/transactions/add-to-app"
      }}>{`Add Transactions to your App`}</a>{`.`}</p>
    <p>{`If you're ready to launch to Production, see the Launch Center.`}</p>
    <Actions.Wrapper>
  <Actions.Card icon={<Rocket mdxType="Rocket" />} title='Launch Center' subtitle="See next steps to launch in Production" link='https://dashboard.plaid.com/developers/launch-center' linkText='Launch' />
    </Actions.Wrapper>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "integration-overview",
        level: 4,
        title: "Integration overview",
      },
{
        id: "transactions-updates",
        level: 4,
        title: "Transactions updates",
      },
{
        id: "recurring-transactions",
        level: 4,
        title: "Recurring transactions",
      },
{
        id: "sample-app-demo-and-code",
        level: 4,
        title: "Sample app demo and code",
      },
{
        id: "testing-transactions-in-sandbox",
        level: 4,
        title: "Testing Transactions in Sandbox",
      },
{
        id: "transactions-pricing",
        level: 4,
        title: "Transactions pricing",
      },
{
        id: "next-steps",
        level: 4,
        title: "Next steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
